'use client';

import { Transition } from '@headlessui/react';
import { type FC, Fragment, useEffect, useState } from 'react';

import IconToastError from '../../assets/icons/icon-toast-error.svg?react';
import IconToastSuccess from '../../assets/icons/icon-toast-success.svg?react';

export interface ToastModel {
  title: string;
  description?: string;
  state: 'default' | 'success' | 'error';
  hasCloseButton?: boolean;
  itemKey?: string;
  position?:
    | 'top-center'
    | 'bottom-center'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
}

interface ToastUIProps {
  toast: ToastModel;
  onClickClose: (toast: ToastModel) => void;
}

export const ToastUI: FC<ToastUIProps> = ({ toast, onClickClose }) => {
  const renderIcon = (toast: ToastModel) => {
    switch (toast.state) {
      case 'success':
        return <IconToastSuccess />;
      case 'error':
        return <IconToastError />;
      default:
        return null;
    }
  };
  return (
    <div className="pointer-events-auto z-100 w-[384px] mobile:w-[320px] overflow-hidden rounded-lg bg-NeutralGray-900 shadow-lg">
      <div className="flex justify-between gap-[16px] p-[16px] rounded-[8px]">
        <div className="flex items-center justify-start">
          {toast.state !== 'default' && (
            <div className="mr-[12px]">{renderIcon(toast)}</div>
          )}
          <div>
            <h1 className="text-sm text-white leading-[18px]">{toast.title}</h1>
            {toast.description && (
              <p className="text-sm text-NeutralGray-300 leading-[18px] mt-[4px]">
                {toast.description}
              </p>
            )}
          </div>
        </div>
        {toast.hasCloseButton && (
          <button
            className="text-sm text-white font-bold leading-[18px]"
            onClick={() => onClickClose(toast)}
          >
            Undo
          </button>
        )}
      </div>
    </div>
  );
};

interface Props {
  toast: ToastModel;
  closeToast: (_key: string) => void;
}

const getPositionClasses = (position: ToastModel['position']) => {
  switch (position) {
    case 'top-left':
      return 'top-[80px] left-[24px]';
    case 'top-right':
      return 'top-[80px] right-[24px]';
    case 'bottom-left':
      return 'bottom-[24px] left-[24px]';
    case 'bottom-right':
      return 'bottom-[24px] right-[24px]';
    case 'bottom-center':
      return 'bottom-[24px] inset-x-0';
    case 'top-center':
    default:
      return 'top-[80px] inset-x-0';
  }
};

const getAlignmentClasses = (position: ToastModel['position']) => {
  switch (position) {
    case 'top-left':
    case 'bottom-left':
      return 'items-start';
    case 'top-right':
    case 'bottom-right':
      return 'items-end';
    case 'top-center':
    case 'bottom-center':
    default:
      return 'items-center';
  }
};

const getAnimationClasses = (position: ToastModel['position']) => {
  switch (position) {
    case 'top-left':
    case 'bottom-left':
      return {
        enter: 'transition duration-500 ease-out',
        enterFrom: 'translate-x-[-100%] opacity-0',
        enterTo: 'translate-x-0 opacity-100',
        leave: 'transition duration-500 ease-in',
        leaveFrom: 'translate-x-0 opacity-100',
        leaveTo: 'translate-x-[-100%] opacity-0',
      };
    case 'top-right':
    case 'bottom-right':
      return {
        enter: 'transition duration-500 ease-out',
        enterFrom: 'translate-x-[100%] opacity-0',
        enterTo: 'translate-x-0 opacity-100',
        leave: 'transition duration-500 ease-in',
        leaveFrom: 'translate-x-0 opacity-100',
        leaveTo: 'translate-x-[100%] opacity-0',
      };
    case 'bottom-center':
      return {
        enter: 'transition duration-500 ease-out',
        enterFrom: 'translate-y-[56px] opacity-0',
        enterTo: 'translate-y-0 opacity-100',
        leave: 'transition duration-500 ease-in',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0 scale-90',
      };
    case 'top-center':
    default:
      return {
        enter: 'transition duration-500 ease-out',
        enterFrom: 'translate-y-[-56px] opacity-0',
        enterTo: 'translate-y-0 opacity-100',
        leave: 'transition duration-500 ease-in',
        leaveFrom: 'opacity-100',
        leaveTo: 'opacity-0 scale-90',
      };
  }
};

const Toast: FC<Props> = ({ toast, closeToast }) => {
  const [isOpened, setIsOpened] = useState(false);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | number>();

  const position = toast.position ?? 'top-center';
  const positionClasses = getPositionClasses(position);
  const alignmentClasses = getAlignmentClasses(position);
  const animationClasses = getAnimationClasses(position);

  useEffect(() => {
    setIsOpened(true);
  }, []);

  useEffect(() => {
    if (isOpened) {
      const timeoutId = setTimeout(() => setIsOpened(false), 2500);
      setTimeoutId(timeoutId);
    }
  }, [isOpened]);

  const onClickClose = (toast: ToastModel) => {
    if (!toast.itemKey) return;

    setIsOpened(false);
    clearTimeout(timeoutId);
    closeToast(toast.itemKey);
  };

  const onToastLeave = (toast: ToastModel) => {
    if (!toast.itemKey) return;

    closeToast(toast.itemKey);
  };

  return (
    <div
      key={toast.itemKey}
      className={`pointer-events-none fixed z-[999] ${positionClasses}`}
    >
      <div className={`flex w-full flex-col ${alignmentClasses}`}>
        <Transition
          show={isOpened}
          as={Fragment}
          enter={animationClasses.enter}
          enterFrom={animationClasses.enterFrom}
          enterTo={animationClasses.enterTo}
          leave={animationClasses.leave}
          leaveFrom={animationClasses.leaveFrom}
          leaveTo={animationClasses.leaveTo}
          afterLeave={() => onToastLeave(toast)}
        >
          <Transition.Child>
            <ToastUI toast={toast} onClickClose={onClickClose} />
          </Transition.Child>
        </Transition>
      </div>
    </div>
  );
};

export default Toast;
